import type { GameState } from '../../engine/game-state.js';
import { layoutFromMap } from '../../render/three/layout.js';
import type { GameSpec } from '../../render/three/game-app.js';
import type { PileSlot } from '../../render/three/layout.js';
import { drawFromStock, hasAnyMatch, PEAK_IDS, tripeaksRules } from './rules.js';

// Spacing constants - keep relationships derived, not hand-tuned.
// Card width 1, height 1.4. Tiny x-gap so cards aren't perfectly smashed.
const COL = 1.05; // step between adjacent cards in the same row
const ROW = 0.5;  // step between rows (cards still overlap, ~36% visible per row)
// Sub-mm stagger per row - just enough to break z-fighting between overlapping
// rows, not enough to read as visible "lift" off the table. Production card
// games keep tableau cards essentially flat; depth comes from sort order and
// the unified contact-shadow pass, not Y separation.
const Y_LIFT = 0.0015;
const R1_Z = 0;
const R2_Z = R1_Z + ROW;
const R3_Z = R1_Z + ROW * 2;
const R4_Z = R1_Z + ROW * 3;
// Row-4 bottom edge sits at R4_Z + CARD_HEIGHT/2 = R4_Z + 0.7; clear it by 0.25.
const STOCK_Z = R4_Z + 0.7 + 0.25 + 0.7; // = R4_Z + 1.65

const slotMap: Record<string, PileSlot> = {
  // Row 1 - peak tops (each peak is centered above its 2 row-2 cards)
  p11: { x: -3 * COL, z: R1_Z, y: 0, fanDown: false },
  p12: { x: 0, z: R1_Z, y: 0, fanDown: false },
  p13: { x: 3 * COL, z: R1_Z, y: 0, fanDown: false },
  // Row 2 - 2 cards per peak, half-step out from peak top
  p21: { x: -3.5 * COL, z: R2_Z, y: Y_LIFT, fanDown: false },
  p22: { x: -2.5 * COL, z: R2_Z, y: Y_LIFT, fanDown: false },
  p23: { x: -0.5 * COL, z: R2_Z, y: Y_LIFT, fanDown: false },
  p24: { x:  0.5 * COL, z: R2_Z, y: Y_LIFT, fanDown: false },
  p25: { x:  2.5 * COL, z: R2_Z, y: Y_LIFT, fanDown: false },
  p26: { x:  3.5 * COL, z: R2_Z, y: Y_LIFT, fanDown: false },
  // Row 3 - 3 cards per peak, half-step out from row 2
  p31: { x: -4 * COL, z: R3_Z, y: Y_LIFT * 2, fanDown: false },
  p32: { x: -3 * COL, z: R3_Z, y: Y_LIFT * 2, fanDown: false },
  p33: { x: -2 * COL, z: R3_Z, y: Y_LIFT * 2, fanDown: false },
  p34: { x: -1 * COL, z: R3_Z, y: Y_LIFT * 2, fanDown: false },
  p35: { x:  0,        z: R3_Z, y: Y_LIFT * 2, fanDown: false },
  p36: { x:  1 * COL, z: R3_Z, y: Y_LIFT * 2, fanDown: false },
  p37: { x:  2 * COL, z: R3_Z, y: Y_LIFT * 2, fanDown: false },
  p38: { x:  3 * COL, z: R3_Z, y: Y_LIFT * 2, fanDown: false },
  p39: { x:  4 * COL, z: R3_Z, y: Y_LIFT * 2, fanDown: false },
  // Row 4 - 10 base cards face-up, half-step out from row 3
  p41:    { x: -4.5 * COL, z: R4_Z, y: Y_LIFT * 3, fanDown: false },
  p42:    { x: -3.5 * COL, z: R4_Z, y: Y_LIFT * 3, fanDown: false },
  p43:    { x: -2.5 * COL, z: R4_Z, y: Y_LIFT * 3, fanDown: false },
  p44:    { x: -1.5 * COL, z: R4_Z, y: Y_LIFT * 3, fanDown: false },
  p45:    { x: -0.5 * COL, z: R4_Z, y: Y_LIFT * 3, fanDown: false },
  p46:    { x:  0.5 * COL, z: R4_Z, y: Y_LIFT * 3, fanDown: false },
  p47:    { x:  1.5 * COL, z: R4_Z, y: Y_LIFT * 3, fanDown: false },
  p48:    { x:  2.5 * COL, z: R4_Z, y: Y_LIFT * 3, fanDown: false },
  p49:    { x:  3.5 * COL, z: R4_Z, y: Y_LIFT * 3, fanDown: false },
  'p4-10':{ x:  4.5 * COL, z: R4_Z, y: Y_LIFT * 3, fanDown: false },
  // Stock & waste - side-by-side under the peaks
  stock: { x: -0.6, z: STOCK_Z, y: 0, fanDown: false },
  waste: { x:  0.6, z: STOCK_Z, y: 0, fanDown: false },
};

const layout = layoutFromMap(slotMap);

function applyDiscard(state: GameState, fromId: string, cardId: string): boolean {
  const move = { from: fromId, to: 'waste', cardId };
  if (!tripeaksRules.canMove(state, move)) return false;
  const fromPile = state.piles[fromId]!;
  const card = fromPile.cards.pop()!;
  state.piles.waste!.cards.push(card);
  state.movesCount++;
  tripeaksRules.afterMove?.(state, move);
  return true;
}

export const tripeaksSpec: GameSpec = {
  rules: tripeaksRules,
  slotForPile: (id) => layout.slot(id),
  // No drag in TriPeaks - every move is a click.
  dropExclude: () => [...PEAK_IDS, 'stock', 'waste'],
  dragStackIds: () => [],
  autoSendOrder: () => ['waste'],
  maxFanLength: 1,
  onPileClick(state, pile, cardId) {
    if (pile.id === 'stock') {
      if (!drawFromStock(state)) return false;
      state.movesCount++;
      return true;
    }
    if (pile.type === 'peak') {
      return applyDiscard(state, pile.id, cardId);
    }
    return false;
  },
  statusText(state) {
    const stockEmpty = state.piles.stock!.cards.length === 0;
    if (stockEmpty && !hasAnyMatch(state) && !tripeaksRules.isWon(state)) return 'No moves left';
    return null;
  },
  cascadeCardIds(state) {
    return state.piles.waste!.cards.map((c) => c.id);
  },
  // No "card was here" markers - TriPeaks slots aren't refilled, so empty == gone.
  showSlotMarker: () => false,
};
